import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { PublicLayout } from "@/components/PublicLayout";
import { useI18n } from "@/i18n/I18nContext";
import { Seo } from "@/components/Seo";
import { GoogleReviewsSection } from "@/components/GoogleReviews";
import { Button } from "@/components/ui/button";
import { ArrowRight, CalendarDays, MapPin, Flame, Timer, Users, Trophy, CheckCircle2 } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1517836357463-d25dfeac3438?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const FAQS = [
  {
    q: { es: "¿Dónde hacer CrossFit en Coronado?", en: "Where to do CrossFit in Coronado?", fr: "Où faire du CrossFit à Coronado ?" },
    a: { es: "En Frenchies Fitness, en el Coronado Padel Center (Nueva Gorgona). Clases de fitness funcional estilo CrossFit todos los días con coaches certificados, para todos los niveles.",
         en: "At Frenchies Fitness, inside the Coronado Padel Center (Nueva Gorgona). Daily CrossFit-style functional fitness classes with certified coaches, for every level.",
         fr: "Chez Frenchies Fitness, au Coronado Padel Center (Nueva Gorgona). Cours de fitness fonctionnel type CrossFit tous les jours avec des coachs certifiés, pour tous les niveaux." },
  },
  {
    q: { es: "¿Necesito experiencia para empezar CrossFit?", en: "Do I need experience to start CrossFit?", fr: "Faut-il de l'expérience pour commencer le CrossFit ?" },
    a: { es: "No. Cada sesión se adapta a tu nivel (Rookie, Fitness o Legend) y el coach ajusta cargas y movimientos. Empiezas con una clase de prueba.",
         en: "No. Every session is scaled to your level (Rookie, Fitness or Legend) and the coach adjusts loads and movements. Start with a trial class.",
         fr: "Non. Chaque séance est adaptée à ton niveau (Rookie, Fitness ou Legend) et le coach ajuste charges et mouvements. Tu commences par un cours d'essai." },
  },
  {
    q: { es: "¿Cuánto cuesta el CrossFit en Coronado?", en: "How much does CrossFit cost in Coronado?", fr: "Combien coûte le CrossFit à Coronado ?" },
    a: { es: "Clase suelta desde $20, bonos de 5 a 50 clases y membresías ilimitadas desde $115/mes. Todo se reserva y paga en línea.",
         en: "Drop-in from $20, packs of 5 to 50 classes and unlimited memberships from $115/month. Everything is booked and paid online.",
         fr: "Cours à l'unité dès 20 $, cartes de 5 à 50 cours et abonnements illimités dès 115 $/mois. Réservation et paiement en ligne." },
  },
];

export default function CrossfitCoronado() {
  const { tl } = useI18n();
  const navigate = useNavigate();

  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "crossfit-coronado-ldjson";
    script.textContent = JSON.stringify({
      "@context": "https://schema.org", "@type": "FAQPage",
      "mainEntity": FAQS.map((f) => ({ "@type": "Question", "name": f.q.es, "acceptedAnswer": { "@type": "Answer", "text": f.a.es } })),
    });
    document.head.appendChild(script);
    return () => { document.getElementById("crossfit-coronado-ldjson")?.remove(); };
  }, []);

  const PERKS = [
    { icon: Flame, t: { es: "WOD diario programado por coaches", en: "Daily WOD programmed by coaches", fr: "WOD quotidien programmé par les coachs" } },
    { icon: Timer, t: { es: "Fuerza, metcon, gimnasia y cardio", en: "Strength, metcon, gymnastics & cardio", fr: "Force, metcon, gymnastique & cardio" } },
    { icon: Users, t: { es: "Grupos reducidos, 3 niveles", en: "Small groups, 3 levels", fr: "Petits groupes, 3 niveaux" } },
    { icon: Trophy, t: { es: "Seguimiento de récords y progreso en la app", en: "PR tracking & progress in the app", fr: "Suivi des records et de la progression dans l'app" } },
  ];

  return (
    <PublicLayout>
      <Seo
        title="CrossFit à Coronado, Panamá — Frenchies Fitness | Fitness fonctionnel"
        description="CrossFit et fitness fonctionnel à Coronado (Nueva Gorgona, Panamá Oeste) : WOD quotidien, coachs certifiés, 3 niveaux, réservation en ligne. Cours d'essai disponible."
        path="/crossfit-coronado"
        keywords="CrossFit Coronado, CrossFit Panamá Oeste, box CrossFit Coronado, fitness funcional Coronado, WOD Coronado, gym Nueva Gorgona"
      />

      <section className="relative flex min-h-[70vh] w-full items-center overflow-hidden bg-black">
        <img src={HERO_IMG} alt="CrossFit à Coronado — Frenchies Fitness" className="absolute inset-0 h-full w-full object-cover opacity-45" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/30" />
        <div className="relative z-10 mx-auto w-full max-w-4xl px-5 py-20 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mb-3 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.22em] text-gold">
            <MapPin className="h-4 w-4" /> Coronado Padel Center · Nueva Gorgona
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="font-display text-4xl font-black uppercase leading-[0.95] tracking-tight text-white sm:text-5xl lg:text-6xl">
            {tl({ es: "CrossFit en Coronado", en: "CrossFit in Coronado", fr: "CrossFit à Coronado" })}
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.22 }} className="mt-5 max-w-xl text-base text-white/85 md:text-lg">
            {tl({ es: "El box de referencia en Panamá Oeste: WOD diario, coaches certificados y una comunidad que te empuja a superarte.",
                  en: "The go-to box in Panamá Oeste: daily WOD, certified coaches and a community that pushes you further.",
                  fr: "La box de référence à Panamá Oeste : WOD quotidien, coachs certifiés et une communauté qui te pousse à te dépasser." })}
          </motion.p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <motion.button data-testid="crossfit-coronado-book-btn" onClick={() => navigate("/schedule")} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
              className="inline-flex items-center gap-2 rounded-full bg-primary px-8 py-4 text-base font-bold text-primary-foreground shadow-[0_0_34px_hsl(var(--primary)/0.5)]">
              <CalendarDays className="h-5 w-5" />{tl({ es: "Reservar mi WOD", en: "Book my WOD", fr: "Réserver mon WOD" })}<ArrowRight className="h-4 w-4" />
            </motion.button>
            <Button size="lg" variant="outline" data-testid="crossfit-coronado-pricing-btn" onClick={() => navigate("/pricing")} className="rounded-full border-white/40 bg-white/10 text-white hover:bg-white/20">
              {tl({ es: "Ver precios", en: "See pricing", fr: "Voir les tarifs" })}
            </Button>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-5 py-16 lg:px-8">
        <h2 className="font-display text-2xl font-bold tracking-tight md:text-3xl">
          {tl({ es: "CrossFit y fitness funcional en Coronado, Panamá Oeste", en: "CrossFit & functional fitness in Coronado, Panamá Oeste", fr: "CrossFit et fitness fonctionnel à Coronado, Panamá Oeste" })}
        </h2>
        <p className="mt-4 leading-relaxed text-muted-foreground">
          {tl({
            es: "Frenchies Fitness es el box de CrossFit de Coronado. Cada día programamos un WOD completo: fuerza, metcon, gimnasia y cardio, adaptado a tres niveles para que principiantes y atletas entrenen juntos. Estamos en el Coronado Padel Center, Nueva Gorgona, a minutos de Coronado, Gorgona y San Carlos.",
            en: "Frenchies Fitness is Coronado's CrossFit box. Every day we program a complete WOD: strength, metcon, gymnastics and cardio, scaled to three levels so beginners and athletes train together. We're at the Coronado Padel Center, Nueva Gorgona, minutes from Coronado, Gorgona and San Carlos.",
            fr: "Frenchies Fitness est la box de CrossFit de Coronado. Chaque jour, un WOD complet : force, metcon, gymnastique et cardio, décliné en trois niveaux pour que débutants et athlètes s'entraînent ensemble. Nous sommes au Coronado Padel Center, Nueva Gorgona, à quelques minutes de Coronado, Gorgona et San Carlos." })}
        </p>
        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          {PERKS.map(({ icon: Icon, t }, i) => (
            <div key={i} data-testid={`crossfit-perk-${i}`} className="flex items-center gap-3 rounded-2xl border border-border bg-card px-5 py-4">
              <Icon className="h-6 w-6 shrink-0 text-primary" /><span className="font-medium">{tl(t)}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-secondary/40 py-16">
        <div className="mx-auto max-w-4xl px-5 lg:px-8">
          <h2 className="mb-6 font-display text-2xl font-bold tracking-tight md:text-3xl">{tl({ es: "Preguntas frecuentes", en: "FAQ", fr: "Questions fréquentes" })}</h2>
          <div className="space-y-5">
            {FAQS.map((f, i) => (
              <div key={i} data-testid={`crossfit-faq-${i}`} className="rounded-2xl border border-border bg-card p-6">
                <h3 className="font-display text-lg font-semibold">{tl(f.q)}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{tl(f.a)}</p>
              </div>
            ))}
          </div>
          <ul className="mt-8 space-y-3">
            {[
              { es: "Clase de prueba para conocer el box y a los coaches", en: "Trial class to meet the box and the coaches", fr: "Cours d'essai pour découvrir la box et les coachs" },
              { es: "Reserva en línea y cancela dentro del plazo sin perder crédito", en: "Book online and cancel within the window without losing a credit", fr: "Réserve en ligne et annule dans les délais sans perdre de crédit" },
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-3"><CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-primary" /><span className="text-muted-foreground">{tl(item)}</span></li>
            ))}
          </ul>
        </div>
      </section>

      <GoogleReviewsSection compact />

      <section className="mx-auto max-w-5xl px-5 py-16 text-center lg:px-8">
        <h2 className="font-display text-3xl font-bold tracking-tight md:text-4xl">{tl({ es: "Tu primer WOD en Coronado te espera", en: "Your first WOD in Coronado awaits", fr: "Ton premier WOD à Coronado t'attend" })}</h2>
        <Button size="lg" data-testid="crossfit-coronado-cta-btn" onClick={() => navigate("/schedule")} className="mt-7 rounded-full">
          {tl({ es: "Reservar mi clase", en: "Book my session", fr: "Réserver ma séance" })} <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </section>
    </PublicLayout>
  );
}
